import { useRef } from 'react';

/** Two arrays holding the same elements, in order. */
function sameElements(a, b) {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((x, i) => x === b[i]);
}

/** The same field: the same reference, or two arrays holding the same elements (a `?? []` or a `.map` per render). */
const sameField = (a, b) => Object.is(a, b) || sameElements(a, b);

/** The same keys, each holding the same field. */
function sameFields(a, b) {
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length && keys.every((k) => k in b && sameField(a[k], b[k]));
}

/**
 * AN OBJECT BUILT EVERY RENDER THAT KEEPS ITS IDENTITY WHILE EVERY FIELD DOES: the last value returned, as long as
 * the new one has the same keys and each field is the same reference (or an array of the same elements); otherwise
 * the new one. For a context value: it changes exactly when something in it does, with no dependency list to forget
 * a field from (2026-10-31: four hand-written lists had, hidden while other fields churned every render; once those
 * held still, the panels read stale pips and a hidden figure).
 *
 * Shallow on purpose: a field must itself keep its identity while it reads the same (the shell's handlers and lists
 * do - plan shell-contexts). One rebuilt every render makes the whole value new every render: never stale, only slow,
 * and the render budgets (`e2e/render-budget.spec.js`) catch that.
 *
 * @template {Record<string, unknown>} T
 * @param {T} value
 * @returns {T}
 */
export default function useShallowStable(value) {
  const kept = useRef(value);
  if (!sameFields(kept.current, value)) kept.current = value;
  return kept.current;
}
